import JSEncrypt from 'jsencrypt';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useAtom, useSetAtom } from 'jotai';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { paths } from 'src/routes/paths';

import { pushLearnerClick } from 'src/utils/analytics';
import { callAutoLogout } from 'src/utils/autoLoginUtil';
import localStorageUtil from 'src/utils/localStorageUtil';
import { handleDecrypt, handleEncrypt } from 'src/utils/cryptoPassword';
import { storageConstants } from 'src/utils/constants/storageConstants';
import { loginSchemaValidation } from 'src/utils/validations/login-validation';
import { randomColor, getBackgroundColor } from 'src/utils/getBackGroundColorUtil';

import store from 'src/store';
import rsaApi from 'src/api/rsaApi';
import authApi from 'src/api/authApi';
import memberApi from 'src/api/membershipApi';
import i18n, { setUserLanguage } from 'src/i18n';
import { setAuthInHeader } from 'src/api/instance';

import { ResLogin } from 'src/types/auth';

import useUser from './use-user';
import { useRoute } from './use-route';
import useDormancy from './use-dormancy';
import { useBoolean } from './use-boolean';
import useLoginError from './use-login-error';
import useHandleError from './use-handle-error';

const useLogin = () => {
  // location
  const location = useLocation();

  // translation
  const { t } = useTranslation();

  // custom hooks
  const user = useUser();
  const handleError = useHandleError();
  const route = useRoute();
  const dormancy = useDormancy();
  const { handleLoginError } = useLoginError();

  // boolean hooks
  const isSubmitting = useBoolean(false);
  const isDormantUser = useBoolean(false);

  // state (global, scope)
  const [loginState, setLoginState] = useAtom(store.login.state);

  // 멤버 정보 atom
  const {
    memberImageUrlAtom,
    memberNameAtom,
    memberFirstNameAtom,
    memberLastNameAtom,
    memberDutyNameAtom,
    memberDutyRoleNameAtom,
    memberResponsibilityNameAtom,
    memberIdAtom,
    memberRoleList,
    lastLoginAtom,
  } = store.user;

  const setMemberImageUrl = useSetAtom(memberImageUrlAtom);
  const setMemberName = useSetAtom(memberNameAtom);
  const setMemberFirstName = useSetAtom(memberFirstNameAtom);
  const setMemberLastName = useSetAtom(memberLastNameAtom);
  const setMemberDutyName = useSetAtom(memberDutyNameAtom);
  const setMemberDutyRoleName = useSetAtom(memberDutyRoleNameAtom);
  const setMemberResponsibilityName = useSetAtom(memberResponsibilityNameAtom);
  const setMemberId = useSetAtom(memberIdAtom);
  const setLastLogin = useSetAtom(lastLoginAtom);
  const setRoleList = useSetAtom(memberRoleList);
  const setUserData = useSetAtom(store.user.userData);

  const [remember, setRemember] = useState(!!localStorage.getItem(storageConstants.rememberUser));

  const [deviceType, setDeviceType] = useState<string | null>(null);
  const [isLoginCalled, setIsLoginCalled] = useState(0);
  const [socialCode, setSocialCode] = useState<any>(null);

  const methods = useForm<{
    userId: string;
    password: string;
  }>({
    ...loginSchemaValidation(t),
    defaultValues: {
      userId: '',
      password: '',
    },
    mode: 'all',
  });

  const { handleSubmit, getValues } = methods;

  const login = (e: any, loginType: any) => {
    e?.preventDefault();
    const params = setParamsForGeneralLogin(loginType);
    if (loginType) {
      callLoginApi(params);
      return;
    }

    // GA4/GTM 분석용 클릭 이벤트입니다. 아이디 원문은 보내지 않고 입력 여부만 전송합니다.
    pushLearnerClick({
      menu_1depth: 'Login',
      menu_2depth: '일반 로그인',
      screen_type: '로그인',
      click_action: '일반 로그인 시도',
      login_method: 'general',
      input_id_exists: !!getValues('userId'),
      redirect_path_exists: !!localStorage.getItem(storageConstants.redirectPath),
    });

    handleSubmit(() => {
      callLoginApi(params);
    })();
  };

  // set parameter
  const setParamsForGeneralLogin = (loginType: any) => {
    let params = null;
    const device = {
      type: getDeviceOS().toUpperCase(),
    };

    if (loginType === 'social') {
      params = {
        userId: '',
        password: '',
        device,
        social: {
          type: 'firebase',
          token: loginState.socialToken,
        },
      };
    } else {
      params = {
        userId: getValues('userId'),
        password: getValues('password'),
        device,
      };
    }
    return params;
  };

  const setParamsForAutoLogin = (loginType: any, userParams: any) => {
    const device = {
      type: getDeviceOS().toUpperCase(),
    };

    if (loginType === 'social') {
      return {
        userId: '',
        password: '',
        device,
        social: {
          type: 'firebase',
          token: userParams.social.token,
        },
      };
    }
    return {
      userId: userParams.userId,
      password: handleDecrypt(userParams.password),
      device,
    };
  };

  // Function to call login api
  const callLoginApi = handleError(async (params: any) => {
    console.log('[학습자-callLoginApi]');
    isSubmitting.onTrue();
    try {
      // rsa password 생성 및 파라미터 추가
      const rsaKey = await getRsaKey();
      const rsaPassword = makeRsaPassword({ publicKey: rsaKey, password: params?.password });
      const learnerLoginResult = await authApi.login({ ...params, password: rsaPassword });

      // 통합 로그인 상태 검사
      const totalLogin = localStorage.getItem('totalLogin');
      if (totalLogin) {
        console.log('[학습자-callLoginApi] 통합로그인 조건문');
        localStorageUtil.setTotalLoginToken(totalLogin);
      }

      // 1. 로그인 api response 값으로 처리해야 하는 부분 (유저데이터 저장, 토큰 헤더에 저장, 휴면회원 처리)
      if (learnerLoginResult) {
        user.setUserStatusdata(learnerLoginResult.data.data);

        const loginResult = handleLoginResponse(learnerLoginResult, params);
        isDormantUser.setValue(!!loginResult);

        if (!loginResult) additionalLoginCall(learnerLoginResult.data.data);
      }

      // 반환값: 성공 시 응답 객체, 실패 시 null (axios 인터셉터에서 null 반환)
      return learnerLoginResult;
    } catch (error) {
      handleLoginError(error, params);
      return null; // 에러 발생 시 null 반환
    } finally {
      isSubmitting.onFalse();
    }
  });

  const additionalLoginCall = handleError(async (learnerLoginResult: any) => {
    if (!isDormantUser.value) {
      // 2. 메뉴 권한 체크 : 사용자의 메뉴(네비게이션 드로어) 권한 체크 하는 함수
      await checkMenuPermissions();

      // 3. 유저 정보 저장
      await getMembershipInfo();

      // 4. 유저 상태 체크 : src/hooks/use-user.js에서 checkUser 함수 확인 필요합니다. (리다이렉트, 통합로그인, 자동로그인)
      const checkUserResult = await user.checkUser(learnerLoginResult);
      // 유저 상태에 이상이 있으면 false를 반환
      if (!checkUserResult) return;

      // 5. 기타 데이터 저장 : 유저에 관한 데이터 조회 하는 api들 호출 및 데이터 저장
      await getAdditionalData();
    }
  });

  const getDeviceOS = () => {
    const deviceOS = localStorage.getItem(storageConstants.deviceOS);
    return deviceOS === 'undefined' || !deviceOS ? 'WEB' : deviceOS;
  };

  const handleLoginResponse = (response: any, params: any) => {
    checkRememberStatus();
    const responseData: ResLogin = response.data.data;
    const localParams = {
      ...params,
      password: handleEncrypt(params.password),
    };

    localStorage.setItem('userParams', JSON.stringify(localParams));
    dormancy.setDormancyUserDetail(responseData);
    isDormantUser.setValue(responseData.dormancy);

    if (responseData.token) {
      setAuthInHeader(responseData.token);
    }

    if (responseData.dormancy) {
      route.pushNavigate(paths.auth.dormancy);
      return true;
    }

    return undefined;
  };

  const checkRememberStatus = () => {
    if (remember) {
      localStorage.setItem(storageConstants.rememberUser, getValues('userId').trim());
    } else {
      localStorage.removeItem(storageConstants.rememberUser);
    }
  };

  const logout = handleError(async () => {
    const deviceOS = localStorage.getItem(storageConstants.deviceOS);

    await authApi.logout();
    await callAutoLogout();

    // TASK-0224 revision: i18n 메모리 상태 초기화
    // 로그아웃 시 i18n 리소스를 제거하여 다음 로그인 시 서버에서 언어를 다시 받아오도록 함
    const languages = ['ko', 'en', 'ja', 'zh', 'es', 'fr', 'de'];
    languages.forEach((lang) => {
      try {
        i18n.removeResourceBundle(lang, 'translation');
      } catch (error) {
        // 리소스가 없는 경우 무시
      }
    });

    localStorageUtil.removeToken();
    localStorageUtil.init();

    // redirectPath 제거 로직
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('redirectPath'); // redirectPath 제거

    // deviceOS 추가
    if (deviceOS) searchParams.set('deviceOS', deviceOS);

    const finalQuery = searchParams.toString(); // ? 없이 순수 쿼리 문자열
    const queryString = finalQuery ? `?${finalQuery}` : '';

    route.pushNavigate('/', queryString);
  });

  // 권한 체크 함수
  const checkMenuPermissions = handleError(async () => {
    const result = await memberApi.getCheckInfo();
    const {
      permissions,
      currentBrand: { code, imgUrl, name },
    } = result.data.data;
    // 브랜드 정보 저장
    localStorage.setItem(storageConstants.brandCode, code);
    localStorage.setItem(storageConstants.brandName, name);
    localStorage.setItem(storageConstants.brandLogo, imgUrl);
    localStorageUtil.setPermissions(permissions);
  });

  const getAdditionalData = handleError(async () => {
    try {
      const [permissionsResponse, memberResponse, profileImageResponse] = await Promise.all([
        memberApi.getCheckInfo(),
        memberApi.fetchMember(),
        memberApi.getProileImage(),
      ]);

      const permissions = await permissionsResponse.data.data.permissions;
      localStorageUtil.setPermissions(permissions);

      const memberData = memberResponse.data.data;
      const fullName = memberData ? memberData.name.fullName : '';
      const firstCharOfName = fullName.slice(0, 1);
      localStorage.setItem('firstChar', firstCharOfName);

      const color = getBackgroundColor(profileImageResponse);
      localStorage.setItem(storageConstants.bgColors, `background-color: ${color ?? randomColor}`);
    } catch (error) {
      console.log('[학습자] 에러 후 로그아웃이 발생하는 지점 > ', error);

      await logout();
    }
  });

  const getRsaKey = async () => {
    const brandCode = localStorageUtil.getBrandCode();
    const {
      data: {
        data: { publicKey },
      },
    } = await rsaApi.getRsaKey(brandCode ?? '');

    return publicKey;
  };

  const makeRsaPassword = ({
    publicKey,
    password,
  }: Readonly<{ publicKey: string; password: string }>) => {
    const encryptor = new JSEncrypt();
    encryptor.setPublicKey(publicKey);
    const encrypted = encryptor.encrypt(password);
    if (typeof encrypted !== 'string') {
      console.warn('RSA encryption failed.');
      return '';
    }
    return encrypted;
  };

  // 멤버 정보 호출 함수
  const getMembershipInfo = useCallback(async () => {
    const token = localStorageUtil.getToken();
    if (!token) return;

    const response = await memberApi.getMemberInfo();
    const data = response?.data?.data;
    if (!data) return;

    setMemberImageUrl(
      data.profile?.file?.resourceUri || (typeof data.profile === 'string' ? data.profile : '')
    );
    setMemberName(data.name.fullName || '');
    setMemberFirstName(data.name.first || '');
    setMemberLastName(data.name.last || '');
    setMemberDutyName(data.dutyName || '');
    setMemberDutyRoleName(data.dutyRole?.text || '');
    setMemberResponsibilityName(data.responsibilityName || '');
    setMemberId(data.id || '');
    localStorage.setItem(storageConstants.memberId, String(data.id || ''));
    setLastLogin(data.lastLoginTime || '');
    setRoleList(data.roles);
    setUserData(data);

    const pushLanguage = data.option?.pushLanguage;
    if (pushLanguage) {
      await setUserLanguage(pushLanguage);
    }
  }, [
    setMemberImageUrl,
    setMemberName,
    setMemberFirstName,
    setMemberLastName,
    setMemberDutyName,
    setMemberDutyRoleName,
    setMemberResponsibilityName,
    setMemberId,
    setLastLogin,
    setRoleList,
    setUserData,
  ]); // 의존성 추가

  return {
    // login, logout functions
    logout,
    login,
    callLoginApi,

    // other call login function
    handleLoginResponse,
    checkMenuPermissions,
    getAdditionalData,

    // set params
    setParamsForAutoLogin,
    setParamsForGeneralLogin,

    // social code
    socialCode,
    setSocialCode,

    // device type
    deviceType,
    setDeviceType,

    // login called
    isLoginCalled,
    setIsLoginCalled,

    // login state
    loginState,
    setLoginState,

    // other functions
    getDeviceOS,

    // other states
    isDormantUser,
    isSubmitting,

    // remember user
    remember,
    setRemember,

    // hook form methods
    methods,

    // RSA
    getRsaKey,
    makeRsaPassword,

    // membership info -> save atom
    getMembershipInfo,
  };
};

export default useLogin;
